<template>
    <div>
        <div class="mb-4 flex flex-wrap items-center justify-between gap-4">
            <div>
                <h3
                    class="text-lg font-semibold text-gray-900 dark:text-slate-100"
                >
                    Resumo 360°
                </h3>
                <p class="text-sm text-gray-500 dark:text-slate-400">
                    Identidade, volume, confiabilidade e histórico do vendedor
                </p>
            </div>
            <div
                v-if="review.status === 'approved'"
                class="flex items-center gap-2"
            >
                <span
                    class="inline-flex items-center rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-800 dark:bg-green-900 dark:text-green-200"
                >
                    <svg
                        class="mr-1.5 h-4 w-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clip-rule="evenodd"
                        />
                    </svg>
                    Aprovado
                </span>
            </div>
        </div>

        <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <!-- Identidade Card -->
            <div
                class="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                <div class="mb-3 flex items-center gap-2">
                    <svg
                        class="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium text-gray-600 dark:text-slate-400"
                        >Identidade</span
                    >
                </div>
                <div class="space-y-2">
                    <p
                        class="text-base font-semibold text-gray-900 dark:text-slate-100"
                    >
                        {{ review.cashout.user.name }}
                    </p>
                    <p class="text-sm text-gray-600 dark:text-slate-400">
                        {{ review.cashout.user.email }}
                    </p>
                    <div
                        class="flex items-center gap-2 text-xs text-gray-500 dark:text-slate-500"
                    >
                        <svg
                            class="h-4 w-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                        Solicitado em {{ review.human_created_at }}
                    </div>
                </div>
            </div>

            <!-- Volume Card -->
            <div
                class="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                <div class="mb-3 flex items-center gap-2">
                    <svg
                        class="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium text-gray-600 dark:text-slate-400"
                        >Volume</span
                    >
                </div>
                <div class="space-y-2">
                    <p
                        class="text-2xl font-bold text-gray-900 dark:text-slate-100"
                    >
                        {{ $root.formatCurrency(review.cashout.net_value) }}
                    </p>
                    <p class="text-sm text-gray-600 dark:text-slate-400">
                        {{ review.cashout.id }} • {{ review.cashout.status }}
                    </p>
                </div>
            </div>

            <!-- Total Sacado Card -->
            <div
                class="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                <div class="mb-3 flex items-center gap-2">
                    <svg
                        class="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium text-gray-600 dark:text-slate-400"
                        >Total Sacado</span
                    >
                </div>
                <div v-if="withdrawalsData.loading" class="animate-pulse">
                    <div
                        class="mb-2 h-7 rounded bg-gray-200 dark:bg-gray-700"
                    ></div>
                    <div
                        class="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"
                    ></div>
                </div>
                <div v-else class="space-y-2">
                    <p
                        class="text-2xl font-bold text-gray-900 dark:text-slate-100"
                    >
                        {{
                            $root.formatCurrency(
                                withdrawalsData.total?.total_amount || 0,
                            )
                        }}
                    </p>
                    <div class="space-y-1">
                        <p class="text-sm text-gray-600 dark:text-slate-400">
                            {{ withdrawalsData.total?.total_count || 0 }}
                            {{
                                withdrawalsData.total?.total_count === 1
                                    ? "saque"
                                    : "saques"
                            }}
                        </p>
                        <p
                            v-if="withdrawalsData.recent"
                            class="text-xs text-gray-500 dark:text-slate-500"
                        >
                            Últimos 30 dias:
                            {{
                                $root.formatCurrency(
                                    withdrawalsData.recent.total_amount || 0,
                                )
                            }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Idade da Conta Card -->
            <div
                class="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                <div class="mb-3 flex items-center gap-2">
                    <svg
                        class="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium text-gray-600 dark:text-slate-400"
                        >Idade da Conta</span
                    >
                </div>
                <div class="space-y-2">
                    <p
                        class="text-2xl font-bold text-gray-900 dark:text-slate-100"
                    >
                        {{ accountAge }}
                    </p>
                    <div class="space-y-1">
                        <p class="text-sm text-gray-600 dark:text-slate-400">
                            Criada em {{ formattedAccountCreatedAt }}
                        </p>
                        <p class="text-xs" :class="accountAgeStatusClass">
                            {{ accountAgeStatus }}
                        </p>
                    </div>
                </div>
            </div>

            <!-- Métodos de Pagamento Card -->
            <div
                class="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                <div class="mb-3 flex items-center gap-2">
                    <svg
                        class="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium text-gray-600 dark:text-slate-400"
                        >Métodos de Pagamento</span
                    >
                </div>
                <div v-if="paymentMethodsData.loading" class="animate-pulse">
                    <div
                        class="mb-2 h-6 rounded bg-gray-200 dark:bg-gray-700"
                    ></div>
                    <div
                        class="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"
                    ></div>
                </div>
                <div
                    v-else-if="
                        paymentMethodsData.methods &&
                        paymentMethodsData.methods.length > 0
                    "
                    class="space-y-3"
                >
                    <div
                        v-for="method in paymentMethodsData.methods.slice(0, 2)"
                        :key="method.payment_method"
                        class="space-y-1"
                    >
                        <div class="flex justify-between text-sm">
                            <span
                                class="font-medium text-gray-700 dark:text-slate-300"
                                >{{
                                    formatPaymentMethod(method.payment_method)
                                }}</span
                            >
                            <span
                                class="font-semibold text-gray-900 dark:text-slate-100"
                                >{{
                                    formatPercentage(method.percentage)
                                }}%</span
                            >
                        </div>
                        <div
                            class="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-slate-700"
                        >
                            <div
                                class="h-full rounded-full transition-all duration-300"
                                :class="
                                    getPaymentMethodBarClass(
                                        method.payment_method,
                                    )
                                "
                                :style="{
                                    width:
                                        formatPercentage(method.percentage) +
                                        '%',
                                }"
                            ></div>
                        </div>
                    </div>
                </div>
                <div v-else class="text-sm text-gray-500 dark:text-slate-500">
                    Sem dados disponíveis
                </div>
            </div>

            <!-- Comissões Card -->
            <div
                class="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                <div class="mb-3 flex items-center gap-2">
                    <svg
                        class="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium text-gray-600 dark:text-slate-400"
                        >Comissões</span
                    >
                </div>
                <div v-if="commissionData.loading" class="animate-pulse">
                    <div
                        class="mb-2 h-7 rounded bg-gray-200 dark:bg-gray-700"
                    ></div>
                    <div
                        class="h-4 w-3/4 rounded bg-gray-200 dark:bg-gray-700"
                    ></div>
                </div>
                <div v-else-if="commissionData.summary" class="space-y-2">
                    <p
                        class="text-2xl font-bold text-gray-900 dark:text-slate-100"
                    >
                        {{
                            $root.formatCurrency(
                                commissionData.summary.total_value,
                            )
                        }}
                    </p>
                    <div class="space-y-1">
                        <p class="text-sm text-gray-600 dark:text-slate-400">
                            {{ commissionData.summary.total_count }}
                            {{
                                commissionData.summary.total_count === 1
                                    ? "comissão"
                                    : "comissões"
                            }}
                        </p>
                        <p
                            v-if="
                                commissionData.byType &&
                                commissionData.byType.length > 0
                            "
                            class="text-xs text-gray-500 dark:text-slate-500"
                        >
                            {{
                                formatCommissionType(
                                    commissionData.byType[0].type,
                                )
                            }}
                            {{
                                Math.round(
                                    (commissionData.byType[0].count /
                                        commissionData.summary.total_count) *
                                        100,
                                )
                            }}%
                        </p>
                    </div>
                </div>
                <div v-else class="text-sm text-gray-500 dark:text-slate-500">
                    Sem comissões
                </div>
            </div>

            <div
                class="rounded-lg border border-gray-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800"
            >
                <div class="mb-3 flex items-center gap-2">
                    <svg
                        class="h-5 w-5 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span
                        class="text-sm font-medium text-gray-600 dark:text-slate-400"
                        >Confiabilidade</span
                    >
                </div>
                <div class="space-y-3">
                    <div class="flex items-center justify-between">
                        <span class="text-sm text-gray-600 dark:text-slate-400"
                            >Score do vendedor</span
                        >
                        <span
                            class="text-3xl font-bold"
                            :class="riskTextClass(review)"
                        >
                            {{ review.seller?.score || 0 }}
                        </span>
                    </div>
                    <div class="grid grid-cols-3 gap-2 text-center">
                        <div>
                            <p
                                class="text-xl font-bold text-green-600 dark:text-green-400"
                            >
                                {{ review.reviews?.approved || 0 }}
                            </p>
                            <p
                                class="text-xs text-gray-500 dark:text-slate-500"
                            >
                                Aprovados
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xl font-bold text-red-600 dark:text-red-400"
                            >
                                {{ review.reviews?.rejected || 0 }}
                            </p>
                            <p
                                class="text-xs text-gray-500 dark:text-slate-500"
                            >
                                Reprovados
                            </p>
                        </div>
                        <div>
                            <p
                                class="text-xl font-bold text-yellow-600 dark:text-yellow-400"
                            >
                                {{ review.reviews?.warning || 0 }}
                            </p>
                            <p
                                class="text-xs text-gray-500 dark:text-slate-500"
                            >
                                Atenção
                            </p>
                        </div>
                    </div>
                    <div v-if="reviewTotal > 0" class="flex flex-wrap gap-2">
                        <span
                            class="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
                            :class="approvalRateClass"
                        >
                            {{ approvalRate }}% de aprovação em
                            {{ reviewTotal }}
                            {{ reviewTotal === 1 ? "revisão" : "revisões" }}
                        </span>
                    </div>
                    <div
                        v-if="hasAccountManager || review.cashout.user.verified"
                        class="flex items-center gap-2 border-t pt-3 dark:border-slate-700"
                    >
                        <svg
                            class="h-5 w-5 text-green-500"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clip-rule="evenodd"
                            />
                        </svg>
                        <span
                            class="text-sm font-medium text-green-600 dark:text-green-400"
                            >Vendedor Verificado</span
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import CashoutStatus from "../../layouts/CashoutStatus.vue";
import CommissionType from "../../../mixins/CommissionType.vue";
import {
    buildCommissionSummary,
    getRiskTextClass,
} from "../../../utils/reviewHelpers.js";
import { parseBrazilianDate } from "../../../utils/dateHelpers.js";
import axios from "axios";

export default {
    name: "ReviewSummary360",
    components: {
        CashoutStatus,
    },
    mixins: [CommissionType],
    props: {
        review: {
            type: Object,
            required: true,
        },
        isSameDayUrgent: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            commissionData: {
                loading: false,
                summary: null,
                byType: null,
            },
            paymentMethodsData: {
                loading: false,
                methods: null,
            },
            withdrawalsData: {
                loading: false,
                total: null,
                recent: null,
            },
        };
    },
    watch: {
        "review.cashout.id": {
            immediate: true,
            handler(newId) {
                if (newId) {
                    this.loadFinancialData();
                }
            },
        },
    },
    computed: {
        reviewTotal() {
            const stats = this.review?.reviews || {};

            return (
                (stats.approved || 0) +
                (stats.rejected || 0) +
                (stats.warning || 0)
            );
        },
        approvalRate() {
            if (this.reviewTotal === 0) {
                return 0;
            }

            return Math.round(
                ((this.review?.reviews?.approved || 0) / this.reviewTotal) *
                    100,
            );
        },
        approvalRateClass() {
            if (this.approvalRate >= 90) {
                return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
            }

            if (this.approvalRate >= 60) {
                return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
            }

            return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
        },
        hasAccountManager() {
            const accountManager = this.review?.cashout?.user?.account_manager;
            return accountManager && accountManager !== "Não possui";
        },
        accountManagerInfo() {
            return this.review?.cashout?.user?.account_manager || "Não possui";
        },
        accountAge() {
            if (!this.review?.cashout?.user?.created_at) {
                return "Não disponível";
            }

            const createdAt = parseBrazilianDate(
                this.review.cashout.user.created_at,
            );
            if (!createdAt) {
                return "Data inválida";
            }

            const now = new Date();
            const diffTime = Math.abs(now - createdAt);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (diffDays < 30) {
                return `${diffDays} ${diffDays === 1 ? "dia" : "dias"}`;
            } else if (diffDays < 365) {
                const months = Math.floor(diffDays / 30);
                return `${months} ${months === 1 ? "mês" : "meses"}`;
            } else {
                const years = Math.floor(diffDays / 365);
                const remainingMonths = Math.floor((diffDays % 365) / 30);
                if (remainingMonths > 0) {
                    return `${years} ${years === 1 ? "ano" : "anos"} e ${remainingMonths} ${remainingMonths === 1 ? "mês" : "meses"}`;
                }
                return `${years} ${years === 1 ? "ano" : "anos"}`;
            }
        },
        formattedAccountCreatedAt() {
            if (!this.review?.cashout?.user?.created_at) {
                return "Data não disponível";
            }

            const date = parseBrazilianDate(
                this.review.cashout.user.created_at,
            );
            if (!date) {
                // Se não conseguir fazer o parse, retorna a string original apenas com a data
                const dateStr = this.review.cashout.user.created_at;
                return dateStr.split(" ")[0] || dateStr;
            }

            return date.toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
            });
        },
        accountAgeStatus() {
            if (!this.review?.cashout?.user?.created_at) {
                return "";
            }

            const createdAt = parseBrazilianDate(
                this.review.cashout.user.created_at,
            );
            if (!createdAt) {
                return "";
            }

            const now = new Date();
            const diffDays = Math.ceil(
                (now - createdAt) / (1000 * 60 * 60 * 24),
            );

            if (diffDays < 7) {
                return "Conta muito nova";
            } else if (diffDays < 30) {
                return "Conta nova";
            } else if (diffDays < 90) {
                return "Conta recente";
            } else if (diffDays < 180) {
                return "Conta em maturação";
            } else if (diffDays < 365) {
                return "Conta estabelecida";
            } else {
                return "Conta madura";
            }
        },
        accountAgeClass() {
            if (!this.review?.cashout?.user?.created_at) {
                return "text-gray-400";
            }

            const createdAt = parseBrazilianDate(
                this.review.cashout.user.created_at,
            );
            if (!createdAt) {
                return "text-gray-400";
            }

            const now = new Date();
            const diffDays = Math.ceil(
                (now - createdAt) / (1000 * 60 * 60 * 24),
            );

            if (diffDays < 7) {
                return "text-red-600 dark:text-red-400";
            } else if (diffDays < 30) {
                return "text-orange-600 dark:text-orange-400";
            } else if (diffDays < 90) {
                return "text-yellow-600 dark:text-yellow-400";
            } else if (diffDays < 365) {
                return "text-blue-600 dark:text-blue-400";
            } else {
                return "text-green-600 dark:text-green-400";
            }
        },
        accountAgeStatusClass() {
            if (!this.review?.cashout?.user?.created_at) {
                return "";
            }

            const createdAt = parseBrazilianDate(
                this.review.cashout.user.created_at,
            );
            if (!createdAt) {
                return "";
            }

            const now = new Date();
            const diffDays = Math.ceil(
                (now - createdAt) / (1000 * 60 * 60 * 24),
            );

            if (diffDays < 30) {
                return "text-red-600 dark:text-red-400";
            } else if (diffDays < 90) {
                return "text-yellow-600 dark:text-yellow-400";
            } else {
                return "text-green-600 dark:text-green-400";
            }
        },
    },
    methods: {
        statusName(name) {
            switch (name) {
                case "pending":
                    return "Solicitado";
                case "approved":
                    return "Aprovado";
                case "warning":
                    return "Atenção";
                case "rejected":
                    return "Reprovado";
            }
        },
        statusClass(status) {
            switch (status) {
                case "pending":
                case "warning":
                    return "text-yellow-500";
                case "approved":
                    return "text-green-500";
                case "rejected":
                    return "text-red-500";
            }
        },
        commissionSummary(commissionTypes) {
            return buildCommissionSummary(
                commissionTypes,
                this.formatCommissionType,
            );
        },
        riskTextClass(review) {
            return getRiskTextClass(review?.seller?.score);
        },
        formatPaymentMethod(method) {
            const methods = {
                credit_card: "Cartão de Crédito",
                pix: "PIX",
                boleto: "Boleto",
                debit_card: "Cartão de Débito",
                bank_transfer: "Transferência",
            };
            return methods[method] || method;
        },
        getPaymentMethodBarClass(method) {
            const classes = {
                pix: "bg-green-500 dark:bg-green-600",
                credit_card: "bg-blue-500 dark:bg-blue-600",
                debit_card: "bg-indigo-500 dark:bg-indigo-600",
                boleto: "bg-orange-500 dark:bg-orange-600",
                bank_transfer: "bg-purple-500 dark:bg-purple-600",
            };
            return classes[method] || "bg-gray-500 dark:bg-gray-600";
        },
        formatPercentage(value) {
            const num = parseFloat(value);
            if (isNaN(num)) {
                return "0.00";
            }
            return num.toFixed(2);
        },
        async loadFinancialData() {
            const cashoutId = this.review?.cashout?.id;
            if (!cashoutId) return;

            // Load all data in parallel
            await Promise.all([
                this.loadCommissionData(cashoutId),
                this.loadPaymentMethodsData(cashoutId),
                this.loadWithdrawalsData(cashoutId),
            ]);
        },
        async loadCommissionData(cashoutId) {
            this.commissionData.loading = true;
            try {
                const { data } = await axios.get(
                    `/api/cashout/${cashoutId}/review/commission-summary`,
                );
                if (data.data) {
                    this.commissionData.summary = data.data.summary;
                    this.commissionData.byType = data.data.by_type;
                }
            } catch (error) {
                console.error("Failed to load commission data:", error);
            } finally {
                this.commissionData.loading = false;
            }
        },
        async loadPaymentMethodsData(cashoutId) {
            this.paymentMethodsData.loading = true;
            try {
                const { data } = await axios.get(
                    `/api/cashout/${cashoutId}/review/payment-methods`,
                );
                if (data.data) {
                    this.paymentMethodsData.methods = data.data;
                }
            } catch (error) {
                console.error("Failed to load payment methods:", error);
            } finally {
                this.paymentMethodsData.loading = false;
            }
        },
        async loadWithdrawalsData(cashoutId) {
            this.withdrawalsData.loading = true;
            try {
                const { data } = await axios.get(
                    `/api/cashout/${cashoutId}/review/user-withdrawals`,
                );
                if (data.data) {
                    this.withdrawalsData.total = data.data.total_stats;
                    this.withdrawalsData.recent = data.data.recent_stats;
                }
            } catch (error) {
                console.error("Failed to load withdrawals data:", error);
            } finally {
                this.withdrawalsData.loading = false;
            }
        },
    },
};
</script>
